/* A field for a figure, which is not the same thing as a field holding a number.
 *
 * An input driven straight off the value it edits cannot be typed into with a
 * comma. The keystroke arrives, the text is parsed, the parse drops the
 * separator, and the field re-renders without it before the next digit lands —
 * so "169,5689" is entered as 1695689. Every figure in this app is typed by
 * hand off a broker or exchange card, and the keyboard doing the typing puts a
 * comma under the thumb.
 *
 * So the text being typed is kept here, as text, and the parsed figure is
 * handed up. The two are re-synchronised only when the value changes from
 * outside — a slider moved, a quick button pressed, a record reloaded — and
 * never while the field has focus, because that is the case where the app
 * would be correcting the user mid-word. */

import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { decimal } from '../lib/number';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** The figure the app currently holds. Null shows an empty field. */
  value: number | null;
  /** The parsed figure, and the raw text in case the caller needs to tell an
      empty field from a zero — clearing a limit override, for one. */
  onChange: (parsed: number, text: string) => void;
};

/** How the figure is written into the box when it arrives from outside.
    Trailing zeros go, so 169.5689 does not become 169.56890000. */
function show(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return String(Number(value.toFixed(8)));
}

export function NumberField({ value, onChange, className, ...rest }: Props) {
  const [text, setText] = useState(() => show(value));
  const focused = useRef(false);

  useEffect(() => {
    if (focused.current) return;
    /* Only when it actually differs as a number: re-writing "169," into "169"
       on an unrelated re-render would undo a half-typed figure. */
    const mine = decimal(text);
    const theirs = value ?? NaN;
    const same = Number.isFinite(mine) && Number.isFinite(theirs)
      ? Math.abs(mine - theirs) < 1e-9
      : !Number.isFinite(mine) && !Number.isFinite(theirs);
    if (!same) setText(show(value));
  }, [value, text]);

  return (
    <input
      {...rest}
      className={className ?? 'input'}
      data-mono="true"
      inputMode="decimal"
      value={text}
      onFocus={(e) => { focused.current = true; rest.onFocus?.(e); }}
      onBlur={(e) => {
        focused.current = false;
        // Leaving the field tidies what was typed into how the app writes it.
        setText(show(decimal(text)));
        rest.onBlur?.(e);
      }}
      onChange={(e) => {
        setText(e.target.value);
        onChange(decimal(e.target.value), e.target.value);
      }}
    />
  );
}
